import { publish, EVENTS } from '@theme/global';

/**
 * Variant picker. On option change it asks Shopify to re-render the product
 * section for the selected option values and swaps every [data-variant-target]
 * so price, availability, media and buy buttons stay in sync with Liquid.
 */
class VariantPicker extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.section;
    this.url = this.dataset.url;
    this.abortController = null;
    this.addEventListener('change', (event) => {
      if (!event.target.matches('input[type="radio"], select')) return;
      const source = event.target.matches('select') ? event.target.options[event.target.selectedIndex] : event.target;
      const productUrl = source?.dataset.productUrl;
      // Combined listings: the chosen value belongs to another product, load that product instead.
      if (productUrl && productUrl !== this.url) this.swapProduct(productUrl);
      else this.onChange();
    });
  }

  async swapProduct(productUrl) {
    const page = this.parentElement?.closest('[data-section]');
    if (!page) return;
    this.abortController?.abort();
    this.abortController = new AbortController();
    page.classList.add('is-updating');
    const params = new URLSearchParams({ option_values: this.selectedOptionValueIds.join(','), section_id: this.sectionId });
    try {
      const response = await fetch(`${productUrl}?${params}`, { signal: this.abortController.signal });
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      page.querySelectorAll('[data-variant-target]').forEach((target) => {
        const fresh = html.querySelector(`[data-variant-target="${target.dataset.variantTarget}"]`);
        if (fresh) target.replaceWith(fresh);
      });
      const freshVariants = html.querySelector('[data-variants]');
      const currentVariants = page.querySelector('[data-variants]');
      if (freshVariants && currentVariants) currentVariants.replaceWith(freshVariants);
      document.querySelectorAll(`[data-sticky-atc="${this.sectionId}"] [data-variant-target]`).forEach((target) => {
        const fresh = html.querySelector(`[data-sticky-atc] [data-variant-target="${target.dataset.variantTarget}"]`);
        if (fresh) target.replaceWith(fresh);
      });
      const title = html.querySelector('title');
      if (title) document.title = title.textContent;
      const variantId = html.querySelector('[data-variant-id]')?.value;
      const url = new URL(productUrl, window.location.origin);
      if (variantId) url.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', url);
      document.dispatchEvent(new CustomEvent('fivl:content', { detail: { root: page } }));
      publish(EVENTS.variantChange, { sectionId: this.sectionId, variant: null, variantId, productUrl });
    } catch (error) {
      if (error.name !== 'AbortError') console.error(error);
    } finally {
      page.classList.remove('is-updating');
    }
  }

  get selectedOptionValueIds() {
    const ids = [];
    this.querySelectorAll('fieldset').forEach((fieldset) => {
      const radio = fieldset.querySelector('input[type="radio"]:checked');
      const select = fieldset.querySelector('select');
      const source = radio || (select && select.options[select.selectedIndex]);
      if (source) ids.push(source.dataset.optionValueId);
    });
    return ids;
  }

  async onChange() {
    // closest() would match the picker itself, which also carries data-section.
    const page = this.parentElement?.closest('[data-section]');
    if (!page) return;
    this.abortController?.abort();
    this.abortController = new AbortController();
    page.classList.add('is-updating');

    const params = new URLSearchParams({ option_values: this.selectedOptionValueIds.join(','), section_id: this.sectionId });
    try {
      const response = await fetch(`${this.url}?${params}`, { signal: this.abortController.signal });
      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      this.swapTargets(page, html);

      const variantInput = html.querySelector('[data-variant-id]');
      const variantId = variantInput?.value;
      const variants = this.readVariants(page);
      const variant = variants.find((v) => String(v.id) === String(variantId)) || null;
      if (variantId) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variantId);
        window.history.replaceState({}, '', url);
      }
      publish(EVENTS.variantChange, { sectionId: this.sectionId, variant, variantId });
    } catch (error) {
      if (error.name !== 'AbortError') console.error(error);
    } finally {
      page.classList.remove('is-updating');
    }
  }

  swapTargets(page, html) {
    page.querySelectorAll('[data-variant-target]').forEach((target) => {
      const key = target.dataset.variantTarget;
      if (key === 'media-gallery') return; // only replaced when switching products
      const fresh = html.querySelector(`[data-variant-target="${key}"]`);
      if (!fresh) return;
      if (key === 'variant-picker') {
        // Keep focus: only update availability classes and the selected value labels.
        target.querySelectorAll('label').forEach((label) => {
          const freshLabel = fresh.querySelector(`label[for="${label.getAttribute('for')}"]`);
          if (freshLabel) label.classList.toggle('is-unavailable', freshLabel.classList.contains('is-unavailable'));
        });
        target.querySelectorAll('[data-selected-value]').forEach((el, index) => {
          const freshValue = fresh.querySelectorAll('[data-selected-value]')[index];
          if (freshValue) el.textContent = freshValue.textContent;
        });
        target.querySelectorAll('select').forEach((select, index) => {
          const freshSelect = fresh.querySelectorAll('select')[index];
          if (!freshSelect) return;
          Array.from(select.options).forEach((option, i) => {
            const freshOption = freshSelect.options[i];
            if (freshOption) option.textContent = freshOption.textContent;
          });
        });
        return;
      }
      target.replaceWith(fresh);
    });
    // Sticky bar lives outside the section wrapper
    document.querySelectorAll(`[data-sticky-atc="${this.sectionId}"] [data-variant-target]`).forEach((target) => {
      const fresh = html.querySelector(`[data-sticky-atc] [data-variant-target="${target.dataset.variantTarget}"]`);
      if (fresh) target.replaceWith(fresh);
    });
  }

  readVariants(page) {
    try {
      return JSON.parse(page.querySelector('[data-variants]')?.textContent || '[]');
    } catch (error) {
      return [];
    }
  }
}
if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);
